import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";

import { Autocomplete, Avatar, Button, Card, CardContent, IconButton, ListItemText, TextField, Tooltip, Typography } from "@mui/material";
import { Stack } from "@mui/system";

import { useRouter } from "src/routes/hooks";
import { paths } from "src/routes/paths";

import { useAuthContext } from "src/hooks/auth/use-auth-context";
import { useOrgTenant } from "src/hooks/auth/use-org-tenant";
import { useBoolean } from "src/hooks/use-boolean";

import { useTranslate } from "src/locales";
import { useGetCompanyContactLinksQuery } from "src/services/connections/connections.service";
import { useAddContactLinkMutation, useGetOrganizationContactsQuery, useRemoveContactLinkMutation } from "src/services/contacts/contacts.service";
import { ContactLinkEntityType, Contact } from "src/services/contacts/contacts.types";

import { ConfirmDialog } from "src/components/custom-dialog";
import Iconify from "src/components/iconify";
import TruncatedTextField from "src/components/truncate-text/truncated-text-field";
import { EntityLinkType } from "src/services/common.types";

type Props = {
  profileId: string;
}

const DEFAULT_PAGE_OPTIONS = {
  page: 1,
  per_page: 5
};

const PrimaryContactCard = ({ profileId }: Props) => {

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const authContext = useAuthContext();

  const editPrimaryContactControl = useBoolean(false);

  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const router = useRouter();

  const [addContactLink, { isLoading: isAddingContactLinks }] = useAddContactLinkMutation();

  const { data: contactLinks, isLoading: isLoadingLinks } = useGetCompanyContactLinksQuery({
    profile_id: profileId,
    params: DEFAULT_PAGE_OPTIONS
  }, {
    skip: !profileId,
  });

  const { currentData: contacts, isLoading: isContactsLoading } = useGetOrganizationContactsQuery(
    {
      org_id: authContext.organization.id,
      page: DEFAULT_PAGE_OPTIONS,
      search_query: contactSearchQuery
    }
  );

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setContactSearchQuery(value);
    }, 500),
    [setContactSearchQuery]
  );

  const onLinkModalClose = useCallback(() => {
    editPrimaryContactControl.onFalse();
    setSelectedContact(null);
    setContactSearchQuery("");
  }, [editPrimaryContactControl, setSelectedContact, setContactSearchQuery]);

  const handleUpdatePrimaryContact = useCallback(async () => {

    if (!selectedContact) return;

    try {
      await addContactLink({
        data: {
          entity_id: profileId,
          entity_type: ContactLinkEntityType.COMPANY_PROFILE,
          contacts: [{
            type: EntityLinkType.PRIMARY,
            id: selectedContact.id,
          }]
        }
      }).unwrap();

      onLinkModalClose();

      enqueueSnackbar(t('companies.profile.api.update_primary_contact.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.update_primary_contact.default_error'), { variant: 'error' });
    }
  }, [onLinkModalClose, addContactLink, profileId, selectedContact, t]);

  const primaryContactLink = useMemo(() => contactLinks?.results?.find((contactLink) => contactLink.type === EntityLinkType.PRIMARY) ?? null, [contactLinks]);

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} display="flex" alignItems="center">
            <Avatar
              alt={primaryContactLink?.contact?.first_name ?? ""}
              variant="rounded"
              sx={{ width: 48, height: 48 }}
            >
              {
                !primaryContactLink && (
                  <Iconify icon="ep:question-filled" sx={{ width: 32, height: 32 }} />
                )
              }

            </Avatar>

            <Stack direction="row" display="flex" width="100%" justifyContent="space-between">
              <ListItemText
                primary={t('companies.profile.primary_contact.title')}
                secondary={
                  <TruncatedTextField
                    text={primaryContactLink ? `${primaryContactLink.contact.first_name} ${primaryContactLink.contact.last_name}` : t('companies.profile.primary_contact.no_primary_contact')}
                    limit={15}
                    mode="characters"
                    showMoreEnabled={false}
                    variant="body2"
                    onClick={() => {
                      if (primaryContactLink) {
                        router.push(paths.dashboard.contacts.detail(primaryContactLink?.contact.id as string));
                      }
                    }}
                    sx={{ '&:hover': primaryContactLink ? { cursor: 'pointer', textDecoration: 'underline' } : {} }}
                  />
                }
              />

              <Stack direction="row" spacing={2} display="flex" alignItems="center">
                <Tooltip title={t('companies.profile.primary_contact.update_tooltip')} arrow placement="top">
                  <IconButton onClick={editPrimaryContactControl.onTrue}>
                    <Iconify icon="solar:pen-bold" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>

      <ConfirmDialog
        open={editPrimaryContactControl.value}
        title={t('companies.profile.primary_contact.edit')}
        onClose={onLinkModalClose}
        content={
          <Autocomplete
            sx={{
              paddingTop: 2,
            }}
            multiple={false}
            options={contacts?.results as Contact[] ?? []}
            value={selectedContact as Contact}
            onInputChange={(_e, value) => onSearchQueryChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  'companies.profile.primary_contact.search'
                )}
                placeholder={t(
                  'companies.profile.primary_contact.search_placeholder'
                )}
              />
            )}
            onChange={(_e, value: Contact | null) => setSelectedContact(value)}
            renderOption={({ key, ...rest }, option: Contact) => (
              <li key={key} {...rest}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.first_name} ${option.last_name}`}
                  </Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option) => `${option.first_name} ${option.last_name}` as string}
            isOptionEqualToValue={(option, value) =>
              option && (option as Contact).id === (value as Contact).id
            }
            getOptionKey={(option) => (option as Contact).id as string}
          />
        }
        action={
          <Button variant="contained" disabled={!selectedContact} onClick={handleUpdatePrimaryContact}>
            {t('common.update')}
          </Button>
        }
      />
    </Card>
  )

}

export default PrimaryContactCard; 